import { WarningAmberOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
  Tooltip,
  IconButton,
  Collapse,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import GetAppIcon from "@mui/icons-material/GetApp";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import axios from "../../utils/axiosInstance";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { formatReadableDate } from "../../utils/formatDate";
import { useSortableTable } from "../../utils/sortOfTable";

export default function STFleet() {
  const theme = useTheme();
  const [loadingFleets, setLoadingFleets] = useState(false);
  const [fleets, setFleets] = useState([]);
  const [filteredFleets, setFilteredFleets] = useState([]);
  const [fleetsQuery, setFleetsQuery] = useState("");
  const [fleetPage, setFleetsPage] = useState(0);
  const [fleetRowsPerPage, setFleetsRowsPerPage] = useState(10);
  const [fleetExportMenuAnchor, setFleetExportMenuAnchor] = useState(null);
  const [showEmailInfo, setShowEmailInfo] = useState(false);
  const [showPhoneInfo, setShowPhoneInfo] = useState(false);
  const [selectedFleet, setSelectedFleet] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const columnConfig = {
    time: "number",
    unitName: "string",
    unitId: "string",
    ip: "string",
    lastUpdate: "date",
    server: "string",
  };

  const { handleSort, getSortIcon, resetSort } = useSortableTable(
    fleets,
    columnConfig
  );

  const fetchFleets = async () => {
    try {
      setLoadingFleets(true);

      const [fleetsResponse, devicesResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_GATEAWAY}/iot/getST9100List`),
        axios.get(`${process.env.REACT_APP_API_GATEAWAY}/devices`),
      ]);

      if (fleetsResponse.data.status !== "OK") {
        throw new Error("Failed to fetch fleet data");
      }

      const deviceNameMap = {};
      if (devicesResponse.data && Array.isArray(devicesResponse.data)) {
        devicesResponse.data.forEach((device) => {
          deviceNameMap[device.id] = device.name;
        });
      }

      const serverData = fleetsResponse.data.response;
      let fleets = [];

      Object.keys(serverData).forEach((serverKey) => {
        const server = serverData[serverKey];

        Object.keys(server).forEach((unitId) => {
          const fleet = server[unitId];

          fleets.push({
            unitId,
            unitName: deviceNameMap[unitId] || `Unit ${unitId}`,
            time: fleet.time,
            notification: `N/A`,
            ip: fleet.ip,
            lastUpdate: new Date(fleet.time * 1000).toISOString(),
            state: fleet.state,
            server: serverKey,
          });
        });
      });

      setFleets(fleets);
      setFilteredFleets(fleets);
      setLoadingFleets(false);
    } catch (err) {
      console.error("Error fetching fleets:", err);
      setLoadingFleets(false);
    }
  };

  useEffect(() => {
    fetchFleets();
  }, []);

  const onColumnSort = (columnKey) => {
    const sorted = handleSort(columnKey, filteredFleets);
    setFilteredFleets(sorted);
    setFleetsPage(0);
  };

  const handleFleetsExportMenuOpen = (event) => {
    setFleetExportMenuAnchor(event.currentTarget);
  };

  const handleFleetExportMenuClose = () => {
    setFleetExportMenuAnchor(null);
  };

  const handleFleetsSearch = (e) => {
    const q = e.target.value.toLowerCase();
    setFleetsQuery(q);
    resetSort();

    const filtered = fleets.filter((fleet) => {
      return (
        (fleet.siteName || "").toLowerCase().includes(q) ||
        (fleet.unitId || "").toLowerCase().includes(q) ||
        (fleet.ip || "").toLowerCase().includes(q) ||
        (fleet.lastUpdate || "").toLowerCase().includes(q) ||
        (fleet.server || "").toLowerCase().includes(q)
      );
    });
    setFilteredFleets(filtered);
    setFleetsPage(0);
  };

  const handleFleetsChangePage = (event, newPage) => {
    setFleetsPage(newPage);
  };

  const handleFleetsChangeRowsPerPage = (event) => {
    setFleetsRowsPerPage(parseInt(event.target.value, 10));
    setFleetsPage(0);
  };

  const openEmailInfo = (fleetData) => {
    setSelectedFleet(fleetData);
    setShowEmailInfo(true);
  };

  const openPhoneInfo = (fleetData) => {
    setSelectedFleet(fleetData);
    setShowPhoneInfo(true);
  };

  const paginatedFleets = filteredFleets.slice(
    fleetPage * fleetRowsPerPage,
    fleetPage * fleetRowsPerPage + fleetRowsPerPage
  );

  const copyToClipboard = () => {
    const textContent = filteredFleets
      .map((fleet) => {
        return `${fleet.time}\t${fleet.unitName}\t${fleet.unitId}\t${fleet.ip}\t${fleet.lastUpdate}\t${fleet.server}`;
      })
      .join("\n");

    const headers =
      "Connection Date\tUnitName\tUnitId\tip\tLast Update\tserver";

    navigator.clipboard
      .writeText(`${headers}\n${textContent}`)
      .then(() => console.log(`Fleets data copied to clipboard`))
      .catch((err) => console.error("Failed to copy:", err));

    handleFleetExportMenuClose();
    toast.success("Copied fleet history to clipboard");
  };

  const exportToCSV = () => {
    const csvContent = [
      [
        "Connection Date",
        "Unit Name",
        "UnitId",
        "Ip Address",
        "Last Update",
        "server",
      ],
      ...filteredFleets.map((fleet) => [
        fleet.time,
        fleet.unitName,
        fleet.unitId,
        fleet.ip,
        fleet.lastUpdate,
        fleet.server,
      ]),
    ];

    const csvString = csvContent
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Fleet_History_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    link.click();

    handleFleetExportMenuClose();
    toast.success("Exported fleet History to CSV");
  };

  const exportToExcel = () => {
    const headers = [
      "Connection Date",
      "Unit Name",
      "UnitId",
      "Ip Address",
      "Last Update",
      "server",
    ];
    const rows = filteredFleets.map((fleet) => [
      fleet.time,
      fleet.unitName,
      fleet.unitId,
      fleet.ip,
      fleet.lastUpdate,
      fleet.server,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "fleet History");
    XLSX.writeFile(
      workbook,
      `Fleet_History_${new Date().toISOString().split("T")[0]}.xlsx`
    );

    handleFleetExportMenuClose();
    toast.success(`Exported fleet History to Excel`);
  };

  const exportToPDF = () => {
    const doc = new jsPDF("l", "pt", "a4");
    doc.setFontSize(12);
    doc.text("fleet History Report", 40, 30);

    const headers = [
      [
        "Connection Date",
        "Unit Name",
        "UnitId",
        "Ip Address",
        "Last Update",
        "server",
      ],
    ];
    const rows = filteredFleets.map((fleet) => [
      fleet.time,
      fleet.unitName,
      fleet.unitId,
      fleet.ip,
      fleet.lastUpdate,
      fleet.server,
    ]);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 50,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 3,
        overflow: "linebreak",
      },
      headStyles: { fillColor: [211, 47, 47] },
      tableWidth: "auto",
      columnStyles: {
        0: { cellWidth: 120 },
        1: { cellWidth: 80 },
        2: { cellWidth: 300 },
        3: { cellWidth: 100 },
        4: { cellWidth: 100 },
      },
    });

    doc.save(`FleetHistory_${new Date().toISOString().split("T")[0]}.pdf`);
    handleFleetExportMenuClose();
    toast.success(`Exported fleet History to PDF`);
  };

  return (
    <Grid item xs={12} md sx={{ height: "100%", width: "100%" }}>
      <Paper
        elevation={4}
        sx={{
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          position: "relative",
        }}
      >
        {loadingFleets && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              bgcolor: "rgba(187, 183, 183, 0.12)",
              backdropFilter: "blur(10px)",
              zIndex: 10,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress color="#60a5fa" size={70} />
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              fontSize={{ xs: "1.15rem", sm: "1.5rem", md: "1.8rem" }}
              fontWeight="bold"
            >
              ST9100 Fleet
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              color="white"
              sx={{ borderRadius: 0 }}
              startIcon={<GetAppIcon />}
              onClick={handleFleetsExportMenuOpen}
            >
              Export
            </Button>
          </Box>
        </Box>

        <TextField
          label="Search:"
          variant="outlined"
          size="small"
          value={fleetsQuery}
          onChange={handleFleetsSearch}
          fullWidth
          sx={{ mb: 2 }}
        />

        <TableContainer
          sx={{
            flexGrow: 1,
            height: { xs: "50vh", md: "59.7vh" },
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
            overscrollBehaviorY: "none",
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                {/* Mobile expand button column */}
                <TableCell
                  sx={{
                    display: { xs: "table-cell", md: "none" },
                    fontWeight: "bold",
                    textAlign: "center",
                    fontSize: "12px",
                    padding: "12px 8px",
                    width: "50px",
                    color: "white",
                    backgroundColor: "#000",
                  }}
                >
                  Details
                </TableCell>

                <TableCell
                  onClick={() => onColumnSort("time")}
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    color: "white",
                    backgroundColor: "#000",
                    cursor: "pointer",
                    userSelect: "none",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                    "&:hover": { backgroundColor: "#333" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center", // ✅ center content
                      gap: 0.5, // optional spacing between text & icon
                    }}
                  >
                    Connection Date
                    {getSortIcon("time")}
                  </Box>
                </TableCell>

                <TableCell
                  onClick={() => onColumnSort("unitName")}
                  sx={{
                    fontWeight: "bold",
                    color: "white",
                    backgroundColor: "#000",
                    cursor: "pointer",
                    userSelect: "none",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                    "&:hover": { backgroundColor: "#333" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center", // ✅ center content
                      gap: 0.5, // optional spacing between text & icon
                    }}
                  >
                    Unit Name
                    {getSortIcon("unitName")}
                  </Box>
                </TableCell>

                <TableCell
                  onClick={() => onColumnSort("unitId")}
                  sx={{
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: "bold",
                    color: "white",
                    backgroundColor: "#000",
                    cursor: "pointer",
                    userSelect: "none",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                    "&:hover": { backgroundColor: "#333" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center", // ✅ center content
                      gap: 0.5, // optional spacing between text & icon
                    }}
                  >
                    UnitId
                    {getSortIcon("unitId")}
                  </Box>
                </TableCell>

                <TableCell
                  onClick={() => onColumnSort("ip")}
                  sx={{
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: "bold",
                    color: "white",
                    backgroundColor: "#000",
                    cursor: "pointer",
                    userSelect: "none",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                    "&:hover": { backgroundColor: "#333" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center", // ✅ center content
                      gap: 0.5, // optional spacing between text & icon
                    }}
                  >
                    Ip Address
                    {getSortIcon("ip")}
                  </Box>
                </TableCell>

                <TableCell
                  onClick={() => onColumnSort("lastUpdate")}
                  sx={{
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: "bold",
                    color: "white",
                    backgroundColor: "#000",
                    cursor: "pointer",
                    userSelect: "none",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                    "&:hover": { backgroundColor: "#333" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center", // ✅ center content
                      gap: 0.5, // optional spacing between text & icon
                    }}
                  >
                    Last Update
                    {getSortIcon("lastUpdate")}
                  </Box>
                </TableCell>

                <TableCell
                  onClick={() => onColumnSort("server")}
                  sx={{
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: "bold",
                    color: "white",
                    backgroundColor: "#000",
                    cursor: "pointer",
                    userSelect: "none",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                    "&:hover": { backgroundColor: "#333" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center", // ✅ center content
                      gap: 0.5, // optional spacing between text & icon
                    }}
                  >
                    Server
                    {getSortIcon("server")}
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedFleets.length > 0 ? (
                paginatedFleets.map((fleet, index) => (
                  <React.Fragment key={index}>
                    <TableRow
                      hover
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#2f3136" : "#3a3c43",
                        "&:hover": {
                          backgroundColor: "#4a4d54",
                        },
                      }}
                    >
                      {/* Expand Button - Mobile Only */}
                      <TableCell
                        sx={{
                          display: { xs: "table-cell", md: "none" },
                          textAlign: "center",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpansion(index)}
                          sx={{ color: "white" }}
                        >
                          {expandedRows[index] ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </TableCell>

                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        {formatReadableDate(new Date(fleet.time * 1000))}
                      </TableCell>

                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        {fleet.unitName}
                      </TableCell>

                      <TableCell
                        sx={{
                          display: { xs: "none", md: "table-cell" },
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                          textAlign: "center",
                        }}
                      >
                        {fleet.unitId}
                      </TableCell>

                      <TableCell
                        sx={{
                          display: { xs: "none", md: "table-cell" },
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                          textAlign: "center",
                        }}
                      >
                        {fleet.ip}
                      </TableCell>

                      <TableCell
                        sx={{
                          display: { xs: "none", md: "table-cell" },
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                          textAlign: "center",
                        }}
                      >
                        {fleet?.lastUpdate
                          ? formatReadableDate(fleet.lastUpdate)
                          : null}
                      </TableCell>

                      <TableCell
                        sx={{
                          display: { xs: "none", md: "table-cell" },
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                          textAlign: "center",
                        }}
                      >
                        {fleet.server}
                      </TableCell>
                    </TableRow>

                    {/* Mobile Collapsed Row */}
                    <TableRow sx={{ display: { xs: "table-row", md: "none" } }}>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={3}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "#2f3136" : "#3a3c43",
                        }}
                      >
                        <Collapse
                          in={expandedRows[index]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ py: 2, px: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  UnitId:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "white" }}
                                >
                                  {fleet.unitId}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  Ip Address:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "white" }}
                                >
                                  {fleet.ip}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  Last Update:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "white" }}
                                >
                                  {fleet?.lastUpdate
                                    ? formatReadableDate(fleet.lastUpdate)
                                    : "N/A"}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  Server:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "white" }}
                                >
                                  {fleet.server}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    No fleets found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredFleets.length}
          page={fleetPage}
          onPageChange={handleFleetsChangePage}
          rowsPerPage={fleetRowsPerPage}
          onRowsPerPageChange={handleFleetsChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ mt: 1 }}
        />

        <Menu
          anchorEl={fleetExportMenuAnchor}
          open={Boolean(fleetExportMenuAnchor)}
          onClose={handleFleetExportMenuClose}
        >
          <MenuItem onClick={copyToClipboard}>
            <ListItemIcon>
              <FileCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Copy</ListItemText>
          </MenuItem>
          <MenuItem onClick={exportToCSV}>
            <ListItemIcon>
              <DescriptionIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>CSV</ListItemText>
          </MenuItem>
          <MenuItem onClick={exportToExcel}>
            <ListItemIcon>
              <TableViewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Excel</ListItemText>
          </MenuItem>
          <MenuItem onClick={exportToPDF}>
            <ListItemIcon>
              <PictureAsPdfIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>PDF</ListItemText>
          </MenuItem>
        </Menu>
      </Paper>
    </Grid>
  );
}
